import { defineComponent, ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import { NavBar as VanNavBar, Icon as VanIcon, Row as VanRow, Col as VanCol } from 'vant'
import {
  BALL_AMAGE_MAP,
  PlusSvg,
  padBallNumber,
  toDrawBalls,
} from '@/components/lottery-ball-row'
import type { LotteryBall } from '@/components/lottery-ball-row'
import { useRegionStore } from '@/stores/region-store'
import { getApiV1LotteryDetail49208 } from '@/api/sdk.gen'
import type { LotteryDetailDesc, LotteryDetailItem } from '@/api/types.gen'
import { useSeo, useSiteBrand } from '@/composables/use-seo'
import { lotteryName, creatorOrg, partOfSite } from '@/utils/seo'

// ==================== 类型定义 ====================

interface PageData {
  issueNo: string
  periodStr: string
  lotteryTime: string
  balls: LotteryBall[]
  specialBall: LotteryBall
  desc: LotteryDetailDesc
  detailList: LotteryDetailItem[]
}

// ==================== 辅助函数 ====================

/** 把开奖时间（如「2025年06月29日 21:30」或「2025-06-29」）转成 ISO 日期 YYYY-MM-DD，用于 JSON-LD temporalCoverage */
function toIsoDate(s: string): string | undefined {
  const m = /(\d{4})\D+(\d{1,2})\D+(\d{1,2})/.exec(s)
  if (!m) return undefined
  return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`
}

// ==================== 开奖详情页（默认导出） ====================

export default defineComponent({
  name: 'LotteryDetailPage',
  setup() {
  const goBack = useSmartBack()
    const route = useRoute()
    const regionStore = useRegionStore()

    // 支持 ?region=hk|aocai 指定彩种：便于搜索引擎/AI 按指定彩种收录本页（如港彩开奖详情），
    // 并与 /stats、/gallery 等页保持一致；缺省时沿用当前持久化彩种（默认澳彩）。
    const qRegion = route.query.region
    if (qRegion === 'hk' || qRegion === 'aocai') {
      regionStore.setRegion(qRegion)
    }

    const issueNo = route.params.issueNo as string
    const data = ref<PageData | null>(null)
    const loading = ref(true)

    // SEO：开奖结果页带「彩种 + 期号 + 真实号码」动态文案与 Dataset 结构化数据。
    // 期号先从路由参数推导（先于接口），数据就绪后用真实开奖号码补全。
    const brand = useSiteBrand()
    const lotName = computed(() => lotteryName(regionStore.lotteryType))
    const periodNo = computed(() => {
      if (data.value?.periodStr) return data.value.periodStr
      const p = Number.parseInt(issueNo.slice(4), 10)
      return Number.isNaN(p) ? issueNo : String(p)
    })
    const plainNumbers = () => (data.value?.balls ?? []).map((b) => padBallNumber(b.number)).join(',')
    const specialNumber = () =>
      data.value ? padBallNumber(data.value.specialBall.number) : ''

    useSeo({
      title: () => `${lotName.value}第${periodNo.value}期开奖结果_开奖号码公告 - ${brand.value}`,
      description: () => {
        const d = data.value
        if (!d)
          return `${lotName.value}第${periodNo.value}期开奖号码、特码与开奖时间，提供历史开奖记录查询。`
        return `${lotName.value}第${periodNo.value}期开奖号码：平码 ${plainNumbers()}，特码 ${specialNumber()}，开奖时间 ${d.lotteryTime}。提供历史开奖记录查询。`
      },
      keywords: () =>
        `${lotName.value},第${periodNo.value}期,开奖结果,开奖号码,特码,平码,${brand.value}`,
      ogType: 'article',
      jsonLd: () => {
        const d = data.value
        if (!d) return undefined
        const date = toIsoDate(d.lotteryTime || '')
        return {
          '@context': 'https://schema.org',
          '@type': 'Dataset',
          name: `${lotName.value}第${issueNo}期开奖结果`,
          description: `${lotName.value}第${periodNo.value}期开奖号码：平码 ${plainNumbers()}，特码 ${specialNumber()}，开奖时间 ${d.lotteryTime}。`,
          ...(date ? { temporalCoverage: date } : {}),
          creator: creatorOrg(brand.value),
          isPartOf: partOfSite(brand.value),
        }
      },
    })

    onMounted(async () => {
      const year = Number.parseInt(issueNo.slice(0, 4))
      const period = Number.parseInt(issueNo.slice(4))
      const lotteryTypeNum = regionStore.region === 'hk' ? 1 : 2
      const id = year * 10000 + period * 10 + lotteryTypeNum

      const res = await getApiV1LotteryDetail49208({
        query: { region: regionStore.region, id },
      })
      const d = res.data?.data
      if (!d?.record) {
        loading.value = false
        return
      }
      data.value = {
        issueNo,
        periodStr: d.record.periodStr ?? '',
        lotteryTime: d.record.lotteryTime ?? '',
        ...toDrawBalls(d.record.numberList ?? []),
        desc: d.desc ?? {},
        detailList: d.detailList ?? [],
      }
      loading.value = false
    })

    /** 加载中 / 暂无数据 / 详情 */
    function renderBody() {
      if (loading.value) {
        return (
          <div class="py-[.6rem] flex justify-center">
            <div class="w-[.4rem] h-[.4rem] rounded-full border-2 border-[#ddd] border-t-[#07c160] animate-spin" />
          </div>
        )
      }
      if (data.value === null) {
        return <div class="py-[.6rem] text-center text-[.28rem] text-[#999]">暂无数据</div>
      }
      return <DetailContent data={data.value} />
    }

    return () => (
      <div class="lottery-details">
        <VanNavBar fixed title="开奖记录">
          {{
            left: () => (
              <VanIcon name="arrow-left" color="#333" size="16" onClick={() => goBack()} />
            ),
          }}
        </VanNavBar>

        <div class="lottery-index">
          {renderBody()}
        </div>
      </div>
    )
  },
})

// ==================== 详情内容 ====================

const DetailContent = defineComponent({
  props: {
    data: { type: Object as () => PageData, required: true },
  },
  setup(props) {
    return () => {
      const d = props.data
      return (
        <div class="lottery-details-body">
          {/* 期号 + 日期 */}
          <div class="hd-text">
            <VanRow>
              <VanCol span={16}>第{d.periodStr}期最新开奖结果</VanCol>
              <VanCol span={8}>
                <span>{d.lotteryTime}</span>
              </VanCol>
            </VanRow>
          </div>

          {/* 彩球行 — 49208 border-box 结构 */}
          <div class="border-box">
            <div class="border-box-body">
              <div class="border-box-body-bd">
                <VanRow>
                  {d.balls.map((ball) => (
                    <VanCol key={ball.number} class="tp left">
                      <div class={`icon-text bg-amage-${BALL_AMAGE_MAP[ball.color]}`}>
                        <span class="slot-icon">
                          <span>{padBallNumber(ball.number)}</span>
                        </span>
                        <div class="slot-text">
                          {ball.zodiac}/{ball.wuxing}
                        </div>
                      </div>
                    </VanCol>
                  ))}
                  <VanCol class="tc">
                    <div class="connect">
                      <PlusSvg />
                    </div>
                  </VanCol>
                  <VanCol class="tp right">
                    <div class={`icon-text bg-amage-${BALL_AMAGE_MAP[d.specialBall.color]}`}>
                      <span class="slot-icon">
                        <span>{padBallNumber(d.specialBall.number)}</span>
                      </span>
                      <div class="slot-text">
                        {d.specialBall.zodiac}/{d.specialBall.wuxing}
                      </div>
                    </div>
                  </VanCol>
                </VanRow>
              </div>
            </div>
          </div>

          {/* 特码/总和分析 */}
          <div class="details-info">
            <div class="desc">
              <VanRow>
                <VanCol span={12}>特码单双: {d.desc.specialOdevity}</VanCol>
                <VanCol span={12}>
                  <div class="text-right">特码大小: {d.desc.specialSize}</div>
                </VanCol>
              </VanRow>
              <VanRow>
                <VanCol span={12}>总和大小: {d.desc.totalSize}</VanCol>
                <VanCol span={12}>
                  <div class="text-right">总和单双: {d.desc.totalOdevity}</div>
                </VanCol>
              </VanRow>
            </div>

            {/* detailList */}
            {d.detailList.length > 0 && (
              <div class="recommend van-hairline--top mt-[10px] pt-[10px]">
                <div class="bd-info">
                  {/* 玩法名（特码 / 正码 / 正一…）在一期里不重复 */}
                  {d.detailList.map((item) => (
                    <VanRow key={item.key}>
                      <VanCol span={18}>
                        {item.key}: <span>{item.value}</span>
                      </VanCol>
                      <VanCol span={6}>
                        <div class="text-right">
                          {/* 原来是 label，样式在 `.bd-info label` 上，挪到类上；`.bd-info span` 的右外边距要清掉 */}
                          {(item as { right?: number }).right ? (
                            <span class="bg-[#ee4242] text-white text-[12px] px-[0.1rem] rounded-[0.3rem] !mr-0">
                              中奖
                            </span>
                          ) : null}
                        </div>
                      </VanCol>
                    </VanRow>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )
    }
  },
})
